import React, { useEffect, useState } from 'react';
import { useAttendance } from '../context/AttendanceContext';
import './Profile.css';
import configService from '../services/config';
import { useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';

function InfoRow({ label, value, icon }) {
  return (
    <div className="info-row">
      <span className="info-icon">{icon}</span>
      <div className="info-content">
        <span className="info-label">{label}</span>
        <span className="info-value">{value || 'N/A'}</span>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card" style={{ '--stat-color': color }}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

export default function Profile() {
  const { records, leaves, punchedIn } = useAttendance();
  const { userId } = useSelector(state => state.globalVariables);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfileData = async () => {
    try {
      const res = await configService.getAdminDetails({
        organisationalUser: true,
        user_id: userId,
      });

      if (res?.status === 200) {
        // API response direct data object hai ya array, uske hisaab se handle karein
        setProfile(res.data); 
      }
    } catch (error) {
      console.log("Fetch Profile Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [userId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Fallback if profile not found
  if (!profile) return <div className="profile-root">Profile not found</div>;

  // Initials calculation from API name
  const initials = profile.name 
    ? profile.name.split(' ').map(n => n[0]).join('').toUpperCase() 
    : profile.user_name?.[0]?.toUpperCase() || 'U';

  const presentDays = records.filter(r => r.status === 'Present').length;
  const pendingLeaves = leaves.filter(l => l.status === 'Pending').length;

  return (
    <div className="profile-root fade-in-up">
      {/* Hero Section */}
      <div className="profile-hero page-card">
        <div className="profile-avatar-wrap">
          <div className="profile-avatar">
            {profile.image ? <img src={profile.image} alt="profile" style={{width: '100%', borderRadius: '50%'}} /> : initials}
          </div>
          {punchedIn && <span className="profile-active-badge" title="Currently punched in" />}
        </div>

        <div className="profile-hero-info">
          <h1 className="profile-name" style={{ textTransform: 'capitalize' }}>{profile.name}</h1>
          <p className="profile-designation">
            {profile.designation || 'Staff'} · {profile.user_type || 'User'}
          </p>

          <div className="profile-emp-code">
            <span className="emp-code-badge">{profile.user_name}</span>
            <span className="profile-join">
              Joined{' '}
              {new Date(profile.created_at).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="profile-stats delay-1 fade-in-up">
        <StatCard label="Days Present" value={presentDays} color="#4caf50" />
        <StatCard label="Leave Requests" value={leaves.length} color="#6f53f0" />
        <StatCard label="Pending Leaves" value={pendingLeaves} color="#ff9800" />
        <StatCard
          label="Status"
          value={punchedIn ? 'In Office' : 'Out'}
          color={punchedIn ? '#4caf50' : '#9e9e9e'}
        />
      </div>

      {/* Details Section */}
      <div className="profile-details page-card delay-2 fade-in-up">
        <p className="page-section-title" style={{ marginBottom: '20px', fontWeight: 'bold' }}>Personal & Contact Details</p>

        <div className="info-list">
          <InfoRow label="User ID" value={profile.user_id} icon="🆔" />
          <InfoRow label="Full Name" value={profile.name} icon="👤" />
          <InfoRow label="Username" value={profile.user_name} icon="🔑" />
          <InfoRow label="Mobile Number" value={profile.mobile} icon="📱" />
          <InfoRow label="Email Address" value={profile.email} icon="📧" />
          <InfoRow 
            label="Location/Address" 
            value={`${profile.address}, ${profile.state}, ${profile.Pin || profile.pincode || ''}`} 
            icon="📍" 
          />
        </div>
      </div>
    </div>
  );
}